import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useRouteError } from 'react-router-dom';
import { P2GApp, P2GAppConfiguration } from '@parcel2go/npm-p2g-app';
import ScrollToTop from './scroll';
import { P2GBox, P2GButton, P2GGrid, P2GText } from '@parcel2go/core-ui';
import * as Sentry from '@sentry/react';
import Loading from '~/components/core/progress/loading';
import { capturePromiseException } from '~/lib/metrics';
import {
  ANALYTICS_EDGE_API,
  APP,
  CONTENT_API,
  ENVIRONMENT,
  GTM_ENABLED,
  LOCALES_PATH,
  QUERY_API,
  VERSION,
  WSS,
  LOCAL_TENANT_OVERRIDE,
  TranslationNamespaces,
  TRANSLATION_NAMESPACES_ARRAY
} from '~/lib/constants';
import ConnectionStatus from '~/components/connection-status/connection-status';
import EventEmitter from 'events';
import { useTranslation, Trans } from 'react-i18next';

export function Component() {
  const events = useRef<EventEmitter>(new EventEmitter());
  const [itemsInBasket, setItemsInBasket] = useState<number>(0);

  const settings: P2GAppConfiguration = useMemo(
    () => ({
      app: APP,
      appVersion: VERSION,
      contentApi: CONTENT_API,
      environment: ENVIRONMENT,
      gtmEnabled: GTM_ENABLED,
      localesPath: LOCALES_PATH,
      wss: WSS,
      overrideHeaderBasketUrl: '/order-process/confirm',
      overrideNumberOfItemsInBasket: itemsInBasket,
      queryApi: QUERY_API,
      tenantOverride: LOCAL_TENANT_OVERRIDE,
      translationNamespaces: TRANSLATION_NAMESPACES_ARRAY,
      segmentation: false,
      preventMissingKeyUpdates: false // Set to true if you dont want translations to be updated automatically
    }),
    [itemsInBasket]
  );

  useEffect(() => {
    const onBasketCountChanged = (items: number) => {
      if (items !== itemsInBasket) {
        setItemsInBasket(items);
      }
    };

    const e = events.current;

    e.on('basket_count_updated', onBasketCountChanged);

    return () => {
      e.off('basket_count_updated', onBasketCountChanged);
    };
  }, [events, itemsInBasket]);

  return (
    <ScrollToTop>
      <P2GApp settings={settings} analyticsProps={{ endpoint: ANALYTICS_EDGE_API }}>
        <Suspense fallback={<Loading />}>
          <P2GBox
            sx={(x) => ({
              backgroundColor: x.palette?.grey?.A100,
              minHeight: '50vh',
              paddingTop: x.spacing(2),
              paddingBottom: x.spacing(2)
            })}
          >
            <Outlet />
          </P2GBox>
          <ConnectionStatus />
        </Suspense>
      </P2GApp>
    </ScrollToTop>
  );
}

export function ErrorBoundary() {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const error = useRouteError();

  useEffect(() => {
    if (error) {
      capturePromiseException(error);
    }
  }, [error]);

  return (
    <Sentry.ErrorBoundary
      fallback={
        <p>
          <Trans t={t} key="ERROR_GENERIC">
            An error has occurred
          </Trans>
        </p>
      }
    >
      <P2GBox p={6}>
        <P2GGrid container direction="column" alignItems="center" spacing={2}>
          <P2GGrid item>
            <P2GText variant="h3">
              <Trans t={t} key="ERROR_OOPS">
                Oops!
              </Trans>
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="h4" component="p" textAlign="center">
              <Trans t={t} key="ERROR_SOMETHING_WRONG">
                Something went a bit wrong.
              </Trans>
              <br />
              <Trans t={t} key="ERROR_APOLOGY">
                We apologise for any inconvenience caused.
              </Trans>
            </P2GText>
          </P2GGrid>

          <P2GGrid item>
            <P2GButton variant="tertiary" onClick={() => (window.location.href = '/')}>
              <Trans t={t} key="ERROR_BACK_TO_HOMEPAGE">
                Back to homepage
              </Trans>
            </P2GButton>
          </P2GGrid>
        </P2GGrid>
      </P2GBox>
    </Sentry.ErrorBoundary>
  );
}

ErrorBoundary.displayName = 'Root Error Boundary';
